import React from "react"
import { NavLink, NavLinkProps } from "react-router-dom"
import { Spinner } from "components/Elements"
import "./Button.scoped.scss"

export const variants: { [key: string]: string } = {
	primary: "c-btn--primary",
	primaryBordered: "c-btn--primary-bordered",
	secondary: "c-btn--secondary",
	secondaryBordered: "c-btn--secondary-bordered",
	destroyBike: "c-btn--destroyBike",
	insaleBike: "c-btn--insaleBike",
}

export const sizes: { [key: string]: string } = {
	default: "",
	small: "c-btn--s",
	medium: "c-btn--m",
	large: "c-btn--l",
}

type IconProps =
	| { startIcon: React.ReactElement; endIcon?: never }
	| { endIcon: React.ReactElement; startIcon?: never }
	| { endIcon?: undefined; startIcon?: undefined }

export type ButtonProps = (
	| React.ButtonHTMLAttributes<HTMLButtonElement>
	| React.LinkHTMLAttributes<HTMLAnchorElement>
	| NavLinkProps
) & {
	link?: string
	disabled?: boolean
	onClick?: React.MouseEventHandler<HTMLButtonElement>
	isLoading?: boolean
	variant?: keyof typeof variants
	size?: keyof typeof sizes
	type?: "submit" | "reset" | "button"
	additionalProps?: { [key: string]: unknown }
} & IconProps

export const Button = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
	(
		{
			type = "button",
			className = "",
			variant = "primary",
			size = "default",
			onClick = null,
			link,
			disabled,
			isLoading = false,
			startIcon,
			endIcon,
			additionalProps = {},
			...props
		},
		ref: React.ForwardedRef<HTMLButtonElement | HTMLAnchorElement>
	) => {
		const Label = () => (
			<>
				{isLoading && <Spinner color={"white"} size={"20px"} />}
				{!isLoading && startIcon}
				{!isLoading && props.children}
				{!isLoading && endIcon}
			</>
		)

		const disabledClassName = `${disabled ? "is-disabled" : ""}`
		const commonClassName = `c-btn ${variants[variant]} ${sizes[size]} ${disabledClassName} ${className}`

		const handleMouseEvent = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			e.preventDefault()
			!isLoading && onClick(e)
		}

		// only external link (ie `https://test.com`) are string
		const isExternal = Boolean(link) && typeof link === "string" && link.startsWith("http")

		if (Boolean(link) && isExternal) {
			return (
				<a
					ref={ref as React.Ref<HTMLAnchorElement>}
					href={link}
					className={commonClassName}
					target="_blank"
					rel="noopener noreferrer"
					{...additionalProps}
				>
					<Label />
				</a>
			)
		}

		if (Boolean(link) && !isExternal) {
			return (
				<NavLink
					ref={ref as React.Ref<HTMLAnchorElement>}
					to={link}
					className={commonClassName}
					{...additionalProps}
				>
					<Label />
				</NavLink>
			)
		}

		return (
			<button
				ref={ref as React.Ref<HTMLButtonElement>}
				type={type}
				className={commonClassName}
				onClick={handleMouseEvent}
				disabled={disabled}
				{...additionalProps}
			>
				<Label />
			</button>
		)
	}
)
